import { cn } from '@/lib/utils'

export type TextShimmerProps = {
  as?: string
  duration?: number
  /** Width of the highlight band as a percentage of the gradient (default 12). */
  spread?: number
  children: React.ReactNode
} & React.HTMLAttributes<HTMLElement>

/**
 * Seamless gradient — edge stops match so the band can slide without exposing gaps.
 * Highlight uses --text-shimmer-highlight (lighter neutral gleam, theme-aware).
 */
function buildShimmerGradient(bandWidth: number) {
  const bandStart = 50 - bandWidth / 2
  const bandEnd = 50 + bandWidth / 2

  return `linear-gradient(
    90deg,
    hsl(var(--muted-foreground)) 0%,
    hsl(var(--muted-foreground)) ${bandStart}%,
    hsl(var(--text-shimmer-highlight)) 50%,
    hsl(var(--muted-foreground)) ${bandEnd}%,
    hsl(var(--muted-foreground)) 100%
  )`
}

export function TextShimmer({
  as = 'span',
  className,
  duration = 2.5,
  spread = 12,
  children,
  style,
  ...props
}: TextShimmerProps) {
  const bandWidth = Math.min(Math.max(spread, 4), 30)
  const Component = as as React.ElementType

  return (
    <Component
      className={cn('animate-shimmer bg-clip-text font-medium text-transparent [-webkit-background-clip:text]', className)}
      style={{
        backgroundImage: buildShimmerGradient(bandWidth),
        backgroundSize: '200% 100%',
        backgroundRepeat: 'repeat-x',
        animationDuration: `${duration}s`,
        ...style,
      }}
      {...props}
    >
      {children}
    </Component>
  )
}
